import { Suspense } from 'react';
import type { JSX, ReactNode } from 'react';
import { lazyWithRetry } from '@/lib/lazy-with-retry';
import { ChunkErrorBoundary } from '@/common/components/ChunkErrorBoundary';
import { ModulePaneSkeleton } from '@/common/components/ModulePaneSkeleton';
import TimelineModule from '@/modules/calendar/TimelineModule';
import PlaceListModule from '@/modules/places/PlaceListModule';
import MapModule from '@/modules/places/MapModule';
import CalendarModule from '@/modules/calendar/CalendarModule';
import PlaceCardModule from '@/modules/places/PlaceDetailModule';
import EventListModule from '@/modules/calendar/EventListModule';
import { NewsPanel } from '@/modules/news/NewsPanel';
import { ShopifyCRMPanel } from '@/modules/shopify-crm/ShopifyCRMPanel';
import { AppAnalyticsPanel } from '@/modules/mixpanel/AppAnalyticsPanel';
import HtmlViewerModule from '@/modules/pages/artifacts/HtmlViewerModule';
import { ReportModule } from '@/modules/pages/report/ReportModule';
import PagesContainerModule from '@/modules/pages/PagesContainerModule';
import HealthContainerModule from '@/modules/health/HealthContainerModule';
import AgentsModule from '@/modules/agents/AgentsModule';
import NotesModule from '@/modules/notes/NotesModule';
import SynthesisModule from '@/modules/synthesis/SynthesisModule';
import type { ModuleRendererProps } from '../types';

const ChannelsModule = lazyWithRetry(() => import('@/modules/channels/ChannelsModule'));

// Lazy-load EmailPanel to break circular dep: EmailPanel → ConversationContext → pluginApi
const LazyEmailPanel = lazyWithRetry(() => import('@/modules/email/EmailPanel').then(m => ({ default: m.EmailPanel })));

// The heaviest feature areas are code-split out of the eager bundle: Drive
// (TipTap editor stack), Tasks (@dnd-kit + task-import-export), Contacts
// (pipeline drag-drop). Cold-render latency is covered by chunk prefetch at
// every entry point — rail hover + idle warmup (SidebarRail), module.focus UI
// actions (apply-ui-actions), and the new-module picker (useModuleCreation) —
// all via LAZY_CHUNK_FACTORIES in lib/prefetch-module.ts; keep that map in
// sync when adding/removing entries here.
const LazyTaskListModule = lazyWithRetry(() => import('@/modules/tasks/TaskListModule'));
const LazyDriveModule = lazyWithRetry(() => import('@/modules/drive/DriveModule'));
const LazyDocGridModule = lazyWithRetry(() => import('@/modules/drive/docs/DocGridModule'));
const LazyFileViewerModule = lazyWithRetry(() => import('@/modules/drive/FileViewerModule'));
const LazyContactListModule = lazyWithRetry(() => import('@/modules/contacts/ContactListModule'));
const LazyClientListModule = lazyWithRetry(() => import('@/modules/contacts/clients/ClientListModule'));

/**
 * Chunk-loading shell shared by every code-split registry entry: an error
 * boundary for failed chunk loads plus a shape-matched skeleton while the
 * chunk streams in.
 */
function LazyPane({ label, variant = 'list', children }: { label: string; variant?: 'list' | 'grid'; children: ReactNode }) {
  return (
    <ChunkErrorBoundary label={label}>
      <Suspense fallback={<ModulePaneSkeleton variant={variant} />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

export const MODULE_COMPONENTS: Record<string, (props: ModuleRendererProps) => JSX.Element> = {
  // Primary modules
  task_list: ({ module, conversationId, filterText }) => (
    <LazyPane label="Tasks">
      <LazyTaskListModule module={module} conversationId={conversationId || ''} filterText={filterText} />
    </LazyPane>
  ),
  event_list: ({ module, conversationId, filterText }) => (
    <EventListModule module={module} conversationId={conversationId || ''} filterText={filterText} />
  ),
  contact_list: ({ module, conversationId }) => (
    <LazyPane label="Contacts">
      <LazyContactListModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  client_list: ({ module, conversationId }) => (
    <LazyPane label="Clients">
      <LazyClientListModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  places: ({ module, conversationId }) => (
    <PlaceListModule module={module} conversationId={conversationId || ''} />
  ),
  place_list: ({ module, conversationId }) => (
    <PlaceListModule module={module} conversationId={conversationId || ''} />
  ),
  drive: ({ module, conversationId }) => (
    <LazyPane label="Drive" variant="grid">
      <LazyDriveModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  // Supplemental
  note_grid: ({ module, conversationId }) => (
    <LazyPane label="Docs" variant="grid">
      <LazyDocGridModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  // kanban type now uses TaskListModule with kanban view
  kanban: ({ module, conversationId }) => (
    <LazyPane label="Tasks">
      <LazyTaskListModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  // crm type now uses ContactListModule
  crm: ({ module, conversationId }) => (
    <LazyPane label="Contacts">
      <LazyContactListModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  map: ({ module, conversationId }) => (
    <MapModule module={module} conversationId={conversationId || ''} />
  ),
  calendar: ({ module, conversationId }) => (
    <CalendarModule module={module} conversationId={conversationId || ''} />
  ),
  file_viewer: ({ module, conversationId }) => (
    <LazyPane label="File viewer">
      <LazyFileViewerModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  place_card: ({ module, conversationId }) => (
    <PlaceCardModule module={module} conversationId={conversationId || ''} />
  ),
  timeline: ({ module, conversationId, filterText }) => (
    <TimelineModule module={module} conversationId={conversationId || ''} filterText={filterText} />
  ),

  // Plugins / integrations rendered as modules
  email: ({ module, conversationId }) => (
    <LazyPane label="Email">
      <LazyEmailPanel
        config={module.config as Record<string, string> ?? {}}
        onOpenConfig={() => {}}
        conversationId={conversationId || ''}
        instanceId={module.id}
      />
    </LazyPane>
  ),
  news: ({ module, conversationId }) => (
    <NewsPanel
      config={module.config as Record<string, string> ?? { instructions: 'Top news across politics, markets, tech, and world events', tickers: 'SPX,NDX,GC,BTC,ETH' }}
      onOpenConfig={() => {}}
      conversationId={conversationId || ''}
      instanceId={module.id}
    />
  ),
  channels: ({ module, conversationId }) => (
    <LazyPane label="Channels">
      <ChannelsModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  'shopify-crm': ({ module, conversationId }) => (
    <ShopifyCRMPanel
      config={module.config as Record<string, string> ?? {}}
      onOpenConfig={() => {}}
      conversationId={conversationId || ''}
      instanceId={module.id}
    />
  ),
  'app-analytics': ({ module, conversationId }) => (
    <AppAnalyticsPanel
      config={module.config as Record<string, string> ?? {}}
      onOpenConfig={() => window.dispatchEvent(new CustomEvent('xavior:open-mixpanel-config', { detail: { moduleId: module.id } }))}
      conversationId={conversationId || ''}
      instanceId={module.id}
    />
  ),
  mixpanel: ({ module, conversationId }) => (
    <AppAnalyticsPanel
      config={module.config as Record<string, string> ?? {}}
      onOpenConfig={() => window.dispatchEvent(new CustomEvent('xavior:open-mixpanel-config', { detail: { moduleId: module.id } }))}
      conversationId={conversationId || ''}
      instanceId={module.id}
    />
  ),
  // Pages container — lists all report and html_viewer modules
  pages: ({ module, conversationId }) => (
    <PagesContainerModule module={module} conversationId={conversationId || ''} />
  ),

  // Health container — timeline of health events (metrics, documents)
  health: ({ module, conversationId }) => (
    <HealthContainerModule module={module} conversationId={conversationId || ''} />
  ),

  // Artifact viewer — sandboxed iframe for agent-generated HTML
  html_viewer: ({ module, conversationId }) => (
    <HtmlViewerModule module={module} conversationId={conversationId} />
  ),

  // Report — AI-generated editorial reports
  report: ({ module }) => <ReportModule module={module} />,

  // Agents — launch AI agents via /agent-jobs
  agents: ({ module, conversationId }) => (
    <AgentsModule module={module} conversationId={conversationId || ''} />
  ),
  notes: ({ module, conversationId }) => (
    <NotesModule module={module} conversationId={conversationId || ''} />
  ),
  synthesis: ({ module, conversationId }) => (
    <SynthesisModule module={module} conversationId={conversationId || ''} />
  ),
};
